import React from 'react';

import ReactSelect, { GroupBase, Props as ReactSelectProps, SelectInstance } from 'react-select';

import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';

import { getCustomStyles } from 'components/DesignSystem/Select/Select.utils';

import styles from './Select.module.scss';

export interface OptionType {
    value: string | number;
    label: string;
    isDisabled?: boolean;
}

export type SelectVariant = 'default' | 'filter';

export interface SelectProps
    extends Omit<ReactSelectProps<OptionType, boolean, GroupBase<OptionType>>, 'styles' | 'classNames'> {
    /**
     * The label for the select
     */
    label?: string;
    /**
     * Helper text to display below the select
     */
    helperText?: string;
    /**
     * If true, the select will indicate an error
     */
    error?: boolean;
    /**
     * If true, the select will be required
     */
    required?: boolean;
    /**
     * Custom class name for the container
     */
    className?: string;
    /**
     * Variant of the select. 'filter' variant applies special styling when a value is selected
     * (tertiary background, quinary border, brand primary text color)
     * @default 'default'
     */
    variant?: SelectVariant;
    /**
     * If true, styles the placeholder with bold (500) font weight and primary text color,
     * making it appear like a selected value (useful for "All" default states)
     */
    boldPlaceholder?: boolean;
    isLoading?: boolean;
}

/**
 * Custom Select component based on react-select
 * Implements design system styling from Figma atoms
 * Supports light and dark themes via CSS variables
 */
export const Select = React.forwardRef(
    <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
        {
            label,
            helperText,
            error = false,
            required = false,
            isDisabled = false,
            className = '',
            id,
            variant = 'filter',
            value,
            boldPlaceholder = false,
            isLoading = false,
            ...props
        }: SelectProps,
        ref: React.LegacyRef<SelectInstance<Option, IsMulti, Group>> | undefined,
    ) => {
        const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

        // Check if there's a selected value
        const hasValue = Array.isArray(value) ? value.length > 0 : value != null;

        // Determine if filter variant styling should be active
        const isFilterActive = variant === 'filter' && hasValue;

        const containerClass = [
            styles.container,
            error ? styles.error : '',
            isDisabled ? styles.disabled : '',
            className,
        ]
            .filter(Boolean)
            .join(' ');

        // Custom styles for react-select using CSS variables
        const customStyles = getCustomStyles({ error, isFilterActive, isDisabled, boldPlaceholder });

        return (
            <div className={containerClass}>
                {label && (
                    <label htmlFor={selectId} className={styles.label}>
                        {label}
                        {required && <span className={styles.required}>*</span>}
                    </label>
                )}

                <ReactSelect
                    id={selectId}
                    styles={customStyles}
                    isDisabled={isDisabled}
                    required={required}
                    menuPortalTarget={document.body}
                    value={value}
                    variant={variant}
                    {...props}
                    ref={ref}
                    isLoading={isLoading}
                    components={{
                        LoadingIndicator: LoaderSpinner,
                        ...props.components, // Preserve any components passed via props
                    }}
                />

                {helperText && <div className={styles.helperText}>{helperText}</div>}
            </div>
        );
    },
);

export default Select;
